import React from 'react';
import { motion } from 'framer-motion';
import QuantitySelector from '../ui/QuantitySelector';
import {
    FaTrash,
    FaHeart,
    FaLeaf,
    FaCheckCircle, FaTimes
} from 'react-icons/fa';
import { Reveal } from '../animations/Reveal';
import PriceDisplay from "../ui/PriceDisplay";

const CartItem = ({
                      item,
                      onUpdateQuantity,
                      onRemove,
                      onMoveToWishlist,
                      compact = false
                  }) => {
    const { id, name, image, variant, price, quantity, inStock, category, originalPrice } = item;

    const handleQuantityChange = (newQuantity) => {
        onUpdateQuantity(id, newQuantity);
    };

    const handleRemove = () => {
        onRemove(id);
    };

    const handleMoveToWishlist = () => {
        onMoveToWishlist(id);
    };

    // Check if mobile
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

    // Calculate display price (use sale price if available)
    const displayPrice = originalPrice && originalPrice > price ? originalPrice : price;
    const hasDiscount = originalPrice && originalPrice > price;

    return (
        <motion.div
            layout
            whileHover={!compact ? { y: -2 } : {}}
            className={`bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-subtle hover:shadow-lift transition-all duration-300 ${
                compact ? 'p-3 sm:p-4' : 'p-4 sm:p-6'
            }`}
        >
            <div className="flex gap-3 sm:gap-4">
                {/* Product Image */}
                <Reveal>
                    <div className="relative flex-shrink-0">
                        <div className={`${compact ? 'w-16 h-16 sm:w-20 sm:h-20' : 'w-20 h-20 sm:w-24 sm:h-24'} rounded-lg sm:rounded-xl overflow-hidden`}>
                            <img
                                src={image}
                                alt={name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?w=400&auto=format&fit=crop';
                                }}
                            />
                        </div>

                        {/* Stock Indicator */}
                        {inStock && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-sage rounded-full flex items-center justify-center">
                                <FaCheckCircle className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                            </div>
                        )}

                        {/* Out of stock indicator */}
                        {!inStock && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-red-500 rounded-full flex items-center justify-center">
                                <FaTimes className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                            </div>
                        )}
                    </div>
                </Reveal>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex flex-col h-full">
                        {/* Top Row - Name and Price */}
                        <div className="flex-1">
                            <div className="flex justify-between items-start mb-2">
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-medium text-sm sm:text-base text-charcoal truncate">
                                        {name}
                                    </h3>
                                    <div className="flex items-center gap-1 sm:gap-2 mt-1">
                                        <span className="text-xs sm:text-sm text-charcoal/60">
                                            {variant}
                                        </span>
                                        <span className="text-xs px-2 py-0.5 bg-nude/30 rounded-full text-charcoal/60">
                                            {category}
                                        </span>
                                    </div>
                                </div>

                                {/* Mobile Price */}
                                <div className="block sm:hidden">
                                    <div className="text-base font-medium text-charcoal">
                                        ${price.toFixed(2)}
                                    </div>
                                    {hasDiscount && (
                                        <div className="text-xs text-charcoal/40 line-through">
                                            ${originalPrice.toFixed(2)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Clean Beauty Badge */}
                            <Reveal delay={0.2}>
                                <div className="inline-flex items-center gap-1 px-2 py-1 bg-sage/10 rounded-full mb-2 sm:mb-3">
                                    <FaLeaf className="w-2 h-2 sm:w-3 sm:h-3 text-sage" />
                                    <span className="text-xs text-sage">Clean Formula</span>
                                </div>
                            </Reveal>
                        </div>

                        {/* Bottom Row - Quantity, Price, Actions */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 sm:pt-4 border-t border-nude/30">
                            {/* Desktop Price */}
                            <div className="hidden sm:block">
                                <div className="flex items-baseline gap-2">
                                    <div className="flex flex-col">
                                        <span className="text-xl font-light text-charcoal">
                                            ₹{price.toFixed(2)}
                                        </span>
                                        {hasDiscount && (
                                            <span className="text-sm text-charcoal/40 line-through">
                                               ₹{originalPrice.toFixed(2)}
                                            </span>
                                        )}
                                    </div>
                                    {!compact && (
                                        <span className="text-sm text-charcoal/40">
                                            each
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Quantity & Actions */}
                            <div className="flex items-center justify-between sm:justify-end w-full sm:w-auto">
                                {/* Quantity Selector */}
                                <div className="flex items-center gap-2 sm:gap-4">
                                    <Reveal delay={0.4}>
                                        <div className="flex items-center border border-nude rounded-full">
                                            <button
                                                onClick={() => handleQuantityChange(quantity - 1)}
                                                disabled={quantity <= 1}
                                                className="px-3 py-1 text-charcoal/60 hover:text-charcoal disabled:opacity-30 disabled:cursor-not-allowed"
                                            >
                                                -
                                            </button>
                                            <span className="px-3 py-1 text-charcoal font-medium">
                                                {quantity}
                                            </span>
                                            <button
                                                onClick={() => handleQuantityChange(quantity + 1)}
                                                disabled={quantity >= 10}
                                                className="px-3 py-1 text-charcoal/60 hover:text-charcoal disabled:opacity-30 disabled:cursor-not-allowed"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </Reveal>

                                    {/* Actions */}
                                    <Reveal delay={0.5}>
                                        <div className="flex items-center gap-2">
                                            {/* Mobile Remove */}
                                            {(compact || isMobile) ? (
                                                <motion.button
                                                    onClick={handleRemove}
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-nude/20 flex items-center justify-center hover:bg-nude/30 transition-colors"
                                                >
                                                    <FaTrash className="w-2 h-2 sm:w-3 sm:h-3 text-charcoal/40" />
                                                </motion.button>
                                            ) : (
                                                <motion.button
                                                    onClick={handleMoveToWishlist}
                                                    whileHover={{ scale: 1.1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center hover:bg-champagne/20 transition-colors"
                                                >
                                                    <FaHeart className="w-3 h-3 text-champagne" />
                                                </motion.button>
                                            )}
                                        </div>
                                    </Reveal>
                                </div>

                                {/* Mobile Subtotal */}
                                <div className="block sm:hidden">
                                    <div className="text-base font-medium text-charcoal">
                                        ${(price * quantity).toFixed(2)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop Line Item Total */}
            {!compact && !isMobile && (
                <Reveal delay={0.6}>
                    <div className="mt-4 pt-4 border-t border-nude/30 flex items-center justify-between">
                        <span className="text-sm text-charcoal/60">Line Total</span>
                        <span className="text-lg font-medium text-charcoal">
                            ${(price * quantity).toFixed(2)}
                        </span>
                    </div>
                </Reveal>
            )}
        </motion.div>
    );
};

export default CartItem;